import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Target, RefreshCw, Loader, CheckCircle2 } from "lucide-react";

/**
 * Active objective panel — bite-sized goal with progress bar and reward.
 * Polls on mount AND on every battleTick from auth context (fires after
 * each refreshState, so progress updates after every battle).
 */
export default function ObjectivePanel() {
  const { battleTick, refreshState } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/objectives/current");
      setData(data);
    } catch (e) {
      // non-blocking
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, battleTick]);

  const claim = async () => {
    setActing(true);
    try {
      const { data: res } = await api.post("/objectives/claim");
      if (res.newly_earned_title) {
        toast.success(`TITLE UNLOCKED · ${res.newly_earned_title}`, {
          description: `+${res.reward.horde} horde · +${((res.reward.reputation ?? 0) * 100).toFixed(1)}% rep`,
        });
      } else {
        toast.success(`+${res.reward.horde} HORDE · OBJECTIVE COMPLETE`);
      }
      await refreshState();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't claim.");
    } finally {
      setActing(false);
    }
  };

  const reroll = async () => {
    setActing(true);
    try {
      await api.post("/objectives/reroll");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reroll.");
    } finally {
      setActing(false);
    }
  };

  if (loading || !data?.objective) {
    return (
      <div className="narrative-panel" data-testid="objective-panel-loading">
        <p className="tac-label flex items-center gap-2">
          <Loader className="w-3 h-3 animate-spin" /> ASSIGNMENT // INCOMING
        </p>
      </div>
    );
  }

  const obj = data.objective;
  const prog = data.progress;

  return (
    <div className="narrative-panel" data-testid="objective-panel">
      <div className="flex items-baseline justify-between mb-3">
        <p className="tac-label flex items-center gap-2">
          <Target className="w-3 h-3 text-blood" /> OBJECTIVE // {obj.label}
        </p>
        <button
          onClick={reroll}
          disabled={acting}
          className="text-muted-h hover:text-blood transition-colors disabled:opacity-30"
          aria-label="Reroll objective"
          data-testid="objective-reroll-button"
        >
          <RefreshCw className={`w-3 h-3 ${acting ? "animate-spin" : ""}`} />
        </button>
      </div>

      <p className="text-sm text-[var(--text-main)] leading-relaxed mb-4" data-testid="objective-description">
        {obj.description}
      </p>

      <div className="flex items-baseline justify-between mb-2">
        <span className="font-mono text-[0.65rem] text-muted-h tracking-wider">
          PROGRESS
        </span>
        <span className="font-mono text-xs text-bone tabular-nums" data-testid="objective-progress">
          {prog.current.toLocaleString()} / {prog.target.toLocaleString()}
        </span>
      </div>
      <div className="bar-track">
        <div className="bar-fill"
             data-testid="objective-progress-bar"
             style={{
               width: `${Math.max(2, prog.pct)}%`,
               background: prog.met
                            ? "var(--bone)"
                            : "linear-gradient(90deg, var(--blood), #8a0a0a)",
             }} />
      </div>

      <div className="flex items-baseline justify-between mt-3">
        <p className="text-[0.7rem] text-muted-h font-mono tracking-wider">
          REWARD · +{obj.reward.horde} HORDE · +{(obj.reward.reputation * 100).toFixed(1)}% REP
          {obj.reward.title && (
            <>
              {" "}·{" "}
              <span className="text-blood tracking-widest">
                TITLE // {obj.reward.title}
              </span>
            </>
          )}
        </p>
        <span className="font-mono text-[0.65rem] text-muted-h">{prog.pct}%</span>
      </div>

      {prog.met && (
        <button
          onClick={claim}
          disabled={acting}
          className="btn-blood w-full mt-4"
          data-testid="objective-claim-button"
        >
          {acting ? <Loader className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
          CLAIM // +{obj.reward.horde} HORDE
        </button>
      )}
    </div>
  );
}
